import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldApi } from 'informed';
import styled from 'styled-components';

import { Input } from './Input';
import { OptionsBlock } from './primitives/SelectBlock';
import { validateEmail } from 'helpers/validators';
import { mailMask } from 'helpers/masks';

const DOMAINS = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'yahoo.com',
  'icloud.com',
  'web.de',
];

export const InputEmail = () => {
  const { t } = useTranslation('glossary');

  const { setValue, validate } = useFieldApi('email');

  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const onFocus = () => setFocused(true);
  const onBlur = () => setTimeout(() => setFocused(false), 300);

  const handleSetValue = (value: string) => {
    setValue(value);
    setInputValue(value);
    validate();
    setFocused(false);
  };

  const options = useMemo(() => {
    const inputValueParts = inputValue.replace(/@+/, '@').split('@');
    const inputValueDomain = inputValueParts[1];

    return DOMAINS.filter((domain) => {
      return domain !== inputValueDomain && domain.startsWith(inputValueDomain);
    }).map((domain) => {
      const value = `${inputValueParts[0]}@${domain}`;
      return { name: value, value };
    });
  }, [inputValue]);

  const isOptionsVisible = useMemo(() => {
    const specialSymbolCount = (inputValue.match(/@/g) || []).length;
    return (
      specialSymbolCount < 2 &&
      inputValue.includes('@') &&
      focused &&
      !!options.length
    );
  }, [focused, inputValue, options]);

  return (
    <ScInputEmail>
      <Input
        autoComplete="off"
        animatedPlaceholder={t('glossary:email')}
        validateOnBlur={!isOptionsVisible}
        field="email"
        validate={validateEmail}
        mask={mailMask}
        onChange={({ target }) => setInputValue(target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {isOptionsVisible && (
        <div className="options">
          <OptionsBlock
            withBorderMode
            maxHeight={options.length > 3 ? 132 : 'none'}
            options={options}
            onChange={(provider) => handleSetValue(provider)}
          />
        </div>
      )}
    </ScInputEmail>
  );
};

export const ScInputEmail = styled.div`
  position: relative;

  & .options {
    position: absolute;
    z-index: 6;
    top: 0;
    left: 0;
    right: 0;
  }
`;
